<template>
  <div
    v-if="mode !== chartMode.NONE"
    class="chart-header"
    :class="{ 'has-sidebar': isFullscreen }">
    <div class="extra">
      <!-- TODO later, we can add a slot there, to allow for more custom controls, depending on the use case -->
      <!-- <select name="" id=""> -->
      <!-- <option value="">Impressions</option> -->
      <!-- <option value="">Spend</option> -->
      <!-- </select> -->
    </div>

    <div
      v-if="isFullscreen"
      class="sidebar">
      <div
        v-if="groupActions && groupActions.length"
        class="groups">
        <p class="title">Actions</p>
        <chart-actions
          :group-actions="groupActions"
          :is-highlighted="false"
          :is-fullscreen="isFullscreen"
          :can-reset="true"
          :local-cache-key="localCacheKey"
          :should-show-daily-cap="shouldShowDailyCap"
          :should-daily-cap-toggle-be-available="shouldDailyCapToggleBeAvailable"
          @show-daily-cap="showDailyCap"
          @chart-option-selected="optionSelected"
          @chart-option-reset="reset" />
      </div>
    </div>

    <div
      v-if="!isFullscreen"
      class="actions">
      <div
        v-if="highlightedGroupActions && highlightedGroupActions.length"
        class="groups">
        <span
          v-if="chartWarnings?.suggestion.isAvailable || displayPlannedDeliveryWarning"
          v-tooltip.left="chartWarningFormatted"
          class="icon">
          <icon-info />
        </span>
        <chart-actions
          :group-actions="highlightedGroupActions"
          :is-highlighted="true"
          :is-fullscreen="isFullscreen"
          :can-reset="true"
          :local-cache-key="localCacheKey"
          :should-show-daily-cap="shouldShowDailyCap"
          :should-daily-cap-toggle-be-available="shouldDailyCapToggleBeAvailable"
          @show-daily-cap="showDailyCap"
          @chart-option-selected="optionSelected"
          @chart-option-reset="reset" />
      </div>
      <div
        v-if="groupActions.length"
        v-click-outside="hideDropdown"
        class="more-actions">
        <button
          class="more"
          :class="{ active: showDropdown }"
          @click="toggleDropdown">
          <span>...</span>
        </button>
        <transition name="squish">
          <div
            v-if="showDropdown"
            class="dropdown">
            <chart-actions
              :group-actions="hiddenGroupActions"
              :is-fullscreen="isFullscreen"
              :is-highlighted="false"
              :can-reset="hasConfigChanged"
              :local-cache-key="localCacheKey"
              :should-show-daily-cap="shouldShowDailyCap"
              :should-daily-cap-toggle-be-available="shouldDailyCapToggleBeAvailable"
              @show-daily-cap="showDailyCap"
              @go-fullscreen="goFullscreen"
              @chart-option-selected="optionSelected"
              @chart-option-reset="reset" />
          </div>
        </transition>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import * as echarts from 'echarts';
  import { computed, ref } from 'vue';

  import ChartActions from '@/common/components/chart/ChartActions.vue';
  import ChartEvents from '@/common/enums/chart-events.enum';
  import ChartMode from '@/common/enums/chart-mode.enum';
  import ChartTimezoneType from '@/common/enums/chart-timezone-type.enum';
  import LocalCacheKeys from '@/common/enums/local-cache-keys.enum';
  import IconInfo from '@/common/icons/monochrome/Info.vue';
  import ChartGroupAction from '@/common/models/chart/ChartGroupAction';

  import { IChartWarning } from './IChartWarning';
  import IShouldDailyCapToggleBeAvailable from './IShouldDailyCapToggleBeAvailable';

  const props = defineProps<{
    actions: ChartGroupAction[];
    chartOptions: echarts.EChartsOption | null;
    chartWarnings: Record<string, IChartWarning> | null;
    shouldShowDailyCap: boolean;
    shouldDailyCapToggleBeAvailable: IShouldDailyCapToggleBeAvailable | null;
    echartsOptions: any | null;
    groupActions: ChartGroupAction[];
    isFullscreen: boolean;
    mode: string;
    templateRef: string | null;
  }>();

  const emit = defineEmits([
    ChartEvents.SHOW_DAILY_CAP,
    ChartEvents.GO_FULLSCREEN,
    ChartEvents.TIMEZONE_SELECTED,
    ChartEvents.CHART_TYPE_UPDATED,
    ChartEvents.CHART_GRANULARITY_UPDATED,
    ChartEvents.OPTION_SELECTED,
    ChartEvents.RESET,
  ]);

  const chartMode = ChartMode;
  const localCacheKey = computed(() => `${LocalCacheKeys.CHART_OPTIONS}.${props.templateRef}`);
  const showDropdown = ref(false);

  const hasConfigChanged = computed(() => JSON.stringify(props.actions) !== JSON.stringify(props.groupActions));
  const hiddenGroupActions = computed(() =>
    props.mode === ChartMode.COMPACT
      ? props.groupActions
      : props.groupActions.filter((action) => !action.isHighlighted),
  );
  const highlightedGroupActions = computed(() =>
    props.mode === ChartMode.VERBOSE ? props.groupActions.filter((action) => action.isHighlighted) : [],
  );
  const currentSelectedTimezone = computed(() =>
    props.groupActions.find((action) => action.type === 'timezone').options.find((option) => option.isSelected),
  );
  const displayPlannedDeliveryWarning = computed(
    () =>
      props.chartWarnings?.plannedDelivery?.isAvailable &&
      currentSelectedTimezone.value.id === ChartTimezoneType.UNIT_LOCAL,
  );
  const chartWarningFormatted = computed(() => {
    let message = '<div>';
    if (displayPlannedDeliveryWarning.value) {
      message += `<span>${props.chartWarnings.plannedDelivery.message}</span><br>`;
    }
    if (props.chartWarnings.suggestion.isAvailable) {
      message += `<span>${props.chartWarnings.suggestion.message}</span>`;
    }
    message += '</div>';
    return message;
  });

  const goFullscreen = () => {
    emit(ChartEvents.GO_FULLSCREEN);
  };

  const hideDropdown = () => {
    showDropdown.value = false;
  };

  const optionSelected = ({ action, option }) => {
    emit(ChartEvents.OPTION_SELECTED, { action, option });
  };

  const reset = () => {
    emit(ChartEvents.RESET);
  };

  const toggleDropdown = () => {
    showDropdown.value = !showDropdown.value;
  };

  const showDailyCap = () => {
    emit(ChartEvents.SHOW_DAILY_CAP);
  };
</script>

<style lang="scss" scoped>
  @use '@/styles/variables' as *;
  .icon {
    margin-right: 1rem;
    svg {
      stroke: var(--primaryColor);
      width: 2.4rem;
      height: 2.4rem;
    }
  }
  .chart-header {
    display: flex;
    position: relative;
    justify-content: space-between;
    flex-direction: row;
    width: 100%;

    &.has-sidebar {
      width: auto;
    }

    .sidebar {
      display: flex;
      flex-direction: column;
      background: #eee;
      border-left: 1px solid $borders;
      width: 100%;

      .title {
        font-weight: bold;
        text-transform: uppercase;
        text-align: center;
        border-bottom: 1px solid $borders;
        padding: 1rem;
        background: #ddd;
      }

      .groups {
        flex-direction: column;
      }
    }

    button {
      border: none;
      box-shadow: none;
      border-radius: 0.5rem;
      cursor: pointer;
      outline: none;
    }

    .actions {
      display: flex;
      position: relative;

      .groups {
        display: flex;
      }

      .dropdown {
        position: absolute;
        min-width: 25rem;
        background: white;
        display: flex;
        flex-direction: column;
        border-radius: 0.5rem;
        box-shadow: $big-shadow;
        right: 0;
        top: 3rem;
        z-index: 1;
      }

      .more-actions {
        display: flex;

        button.more {
          margin-left: 1rem;
          outline: none;
          cursor: pointer;
          border-radius: 0.5rem;
          transition: all 250ms;
          padding: 0.2rem 0.5rem;

          &:hover,
          &.active {
            background: var(--actionsColor);
            color: var(--actionsColorReverse);

            transition: all 500ms;
          }

          span {
            writing-mode: vertical-lr;
            text-orientation: mixed;
            width: 1rem;
            height: 1rem;
            font-weight: bold;
            margin-left: -1px;
          }
        }
      }
    }
  }
</style>
